import {
	useEffect,
	useRef,
	useCallback,
	useState,
	useLayoutEffect,
} from "react";
import { createPortal } from "react-dom";

const getPositionBase = ({
	triggerRect,
	rootRect,
	triggerWidth,
	minWidth,
	fit,
}) => {
	const basePosition = {
		left: triggerRect.left - rootRect.left,
		top: triggerRect.bottom - rootRect.top,
	};

	if (minWidth) {
		basePosition.minWidth = triggerWidth;
	}

	if (fit) {
		basePosition.width = triggerWidth;
	}

	return basePosition;
};

const PortalPopout = ({
	isOpen,
	onClose,
	triggerRef,
	children,
	className = "",
	fit = false,
	minWidth = true,
	zIndex = 10,
	closeOnScroll = true,
	root = document.body,
	viewportPadding = 8,
}) => {
	const popoutRef = useRef(null);
	const popoutWidthRef = useRef(0);
	const frameRef = useRef(null);
	const [position, setPosition] = useState(null);
	const [isPositionReady, setIsPositionReady] = useState(false);

	const getBasePosition = useCallback(() => {
		if (!triggerRef.current || !root) return null;

		const triggerRect = triggerRef.current.getBoundingClientRect();
		const rootRect = root.getBoundingClientRect();
		const triggerWidth = triggerRect.width;

		return getPositionBase({
			triggerRect,
			rootRect,
			triggerWidth,
			minWidth,
			fit,
		});
	}, [triggerRef, root, minWidth, fit]);

	const updatePosition = useCallback(() => {
		if (triggerRef.current && root) {
			const triggerRect = triggerRef.current.getBoundingClientRect();
			const rootRect = root.getBoundingClientRect();
			const triggerWidth = triggerRect.width;
			const measuredPopoutWidth = popoutRef.current?.offsetWidth || 0;
			const popoutWidth = fit
				? triggerWidth
				: popoutWidthRef.current || measuredPopoutWidth || triggerWidth;
			const viewportLeft = rootRect.left + viewportPadding;
			const viewportRight =
				Math.min(rootRect.right, window.innerWidth) - viewportPadding;
			const minLeft = viewportLeft - rootRect.left;
			const maxLeft = Math.max(
				minLeft,
				viewportRight - rootRect.left - popoutWidth
			);
			const newPosition = getPositionBase({
				triggerRect,
				rootRect,
				triggerWidth,
				minWidth,
				fit,
			});

			newPosition.left = Math.min(
				maxLeft,
				Math.max(minLeft, newPosition.left)
			);

			setPosition(newPosition);
		}
	}, [triggerRef, minWidth, fit, root, viewportPadding]);

	const scheduleUpdatePosition = useCallback(() => {
		if (frameRef.current !== null) return;

		frameRef.current = window.requestAnimationFrame(() => {
			frameRef.current = null;
			updatePosition();
		});
	}, [updatePosition]);

	// Update position when opened
	useEffect(() => {
		if (isOpen) {
			setPosition(getBasePosition());
			setIsPositionReady(false);
		} else {
			if (frameRef.current !== null) {
				window.cancelAnimationFrame(frameRef.current);
				frameRef.current = null;
			}
			setPosition(null);
			setIsPositionReady(false);
		}
	}, [isOpen, getBasePosition]);

	// Measure and position once before making popout visible to avoid initial flicker.
	useLayoutEffect(() => {
		if (!isOpen || !position || isPositionReady) return;

		updatePosition();
		setIsPositionReady(true);
	}, [isOpen, position, isPositionReady, updatePosition]);

	useLayoutEffect(() => {
		if (!isOpen || !popoutRef.current) return;

		const popoutElement = popoutRef.current;
		popoutWidthRef.current = popoutElement.offsetWidth;

		if (typeof ResizeObserver === "undefined") return;

		const observer = new ResizeObserver(() => {
			popoutWidthRef.current = popoutElement.offsetWidth;
			scheduleUpdatePosition();
		});
		observer.observe(popoutElement);

		return () => {
			observer.disconnect();
		};
	}, [isOpen, scheduleUpdatePosition]);

	const close = useCallback(() => {
		onClose?.();
	}, [onClose]);

	// Close dropdown when clicking outside
	useEffect(() => {
		if (!isOpen) return;

		const handleClickOutside = (event) => {
			if (
				popoutRef.current &&
				!popoutRef.current.contains(event.target) &&
				triggerRef.current &&
				!triggerRef.current.contains(event.target)
			) {
				close();
			}
		};

		document.addEventListener("mousedown", handleClickOutside);
		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, [isOpen, close, triggerRef]);

	// Close on scroll outside or reposition
	useEffect(() => {
		if (!isOpen) return;

		const handleScroll = (event) => {
			// Don't close if scrolling inside the popup
			if (popoutRef.current && popoutRef.current.contains(event.target)) {
				return;
			}

			if (closeOnScroll) {
				close();
			} else {
				scheduleUpdatePosition();
			}
		};

		window.addEventListener("scroll", handleScroll, true);
		return () => {
			window.removeEventListener("scroll", handleScroll, true);
		};
	}, [isOpen, close, closeOnScroll, scheduleUpdatePosition]);

	useEffect(() => {
		if (!isOpen) return;

		window.addEventListener("resize", scheduleUpdatePosition);
		return () => {
			window.removeEventListener("resize", scheduleUpdatePosition);
		};
	}, [isOpen, scheduleUpdatePosition]);

	useEffect(
		() => () => {
			if (frameRef.current !== null) {
				window.cancelAnimationFrame(frameRef.current);
				frameRef.current = null;
			}
		},
		[]
	);

	if (!isOpen || !position) return null;

	return createPortal(
		<div
			ref={popoutRef}
			className={`absolute mt-1 bg-white border border-grey-light rounded shadow-lg ${className}`}
			style={{
				visibility: isPositionReady ? "visible" : "hidden",
				zIndex,
				"--portal-popout-top": `${Math.max(0, position.top)}px`,
				...position,
			}}
		>
			{children}
		</div>,
		root
	);
};

export default PortalPopout;
